<template>
    <slide-out-panel
        :is-open="isOpen"
        :title="milestoneId ? 'Attach a File' : 'Upload a File'"
        icon="fad fa-cloud-arrow-up"
        @close="close"
    >
        <p class="mb-4 has-text-grey">
            <template v-if="milestoneId">Attach a document or artifact from this milestone to your reflection.</template>
            <template v-else>Upload a file to keep it stored with your course enrollment.</template>
        </p>

        <b-field>
            <b-upload v-model="selectedFile" drag-drop expanded :disabled="uploading">
                <section class="section py-5">
                    <div class="content has-text-centered">
                        <template v-if="selectedFile">
                            <p class="mb-1"><i class="far fa-file fa-2x"></i></p>
                            <p class="mb-1 has-text-weight-bold">{{ selectedFile.name }}</p>
                            <p class="is-size-7 has-text-grey">{{ formatFileSize(selectedFile.size) }}</p>
                        </template>
                        <template v-else>
                            <p class="mb-1"><i class="far fa-cloud-arrow-up fa-2x"></i></p>
                            <p>Drop a file here or click to choose one</p>
                            <p class="is-size-7 has-text-grey">PDF, Office documents, text, and images &middot; up to 25 MB</p>
                        </template>
                    </div>
                </section>
            </b-upload>
        </b-field>

        <progress v-if="uploading" class="progress is-info" :value="uploadProgress" max="100"></progress>

        <b-field v-if="isStem" class="mt-5 mb-4">
            <b-checkbox v-model="stemFile" class="stem-checkbox">
                <strong>This is a STEM activity file</strong>
                <p class="is-size-7 has-text-grey mt-1">
                    STEM activity files document the STEM work you agreed to at setup and are labeled so
                    reviewers can find them when awarding STEM clock hours.
                </p>
            </b-checkbox>
        </b-field>

        <b-message type="is-danger" v-if="errorMessage" class="mt-4">
            {{ errorMessage }}
        </b-message>

        <template #footer>
            <b-button type="is-light" @click="close" :disabled="uploading">Cancel</b-button>
            <b-button type="is-success" @click="upload" :loading="uploading" :disabled="!selectedFile">
                <i class="far fa-arrow-up-from-bracket mr-2"></i>{{ milestoneId ? 'Attach File' : 'Upload File' }}
            </b-button>
        </template>
    </slide-out-panel>
</template>

<script>
// Shared upload drawer for enrollment files. With a milestoneId the upload
// becomes a reflection attachment; without one it's a plain enrollment file.
// Uploads go through the raw axios instance — cachios would key the FormData
// body as '{}' and serve a stale cached response for repeat uploads.
import {axiosInstance} from "@/http_client";
import SlideOutPanel from "@/components/SlideOutPanel";

export default {
    name: 'FileUploadPanel',
    components: {SlideOutPanel},
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        enrollmentId: {
            type: [Number, String],
            required: true
        },
        milestoneId: {
            type: [Number, String],
            default: null
        },
        isStem: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedFile: null,
            stemFile: false,
            uploading: false,
            uploadProgress: 0,
            errorMessage: null
        };
    },
    methods: {
        close() {
            if (this.uploading) return;
            this.$emit('close');
        },
        reset() {
            this.selectedFile = null;
            this.stemFile = false;
            this.uploading = false;
            this.uploadProgress = 0;
            this.errorMessage = null;
        },
        formatFileSize(bytes) {
            if (!bytes && bytes !== 0) return '';
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
            return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        },
        upload() {
            if (!this.selectedFile) return;

            const formData = new FormData();
            formData.append('file', this.selectedFile);
            if (this.milestoneId) formData.append('milestone_id', this.milestoneId);
            if (this.isStem && this.stemFile) formData.append('stem', 'true');

            this.uploading = true;
            this.uploadProgress = 0;
            this.errorMessage = null;
            axiosInstance.post(`/app/enrollments/${this.enrollmentId}/files`, formData, {
                onUploadProgress: (event) => {
                    if (event.total) this.uploadProgress = Math.round((event.loaded / event.total) * 100);
                }
            }).then(() => {
                this.$buefy.toast.open({
                    message: this.milestoneId ? 'File attached!' : 'File uploaded!',
                    type: 'is-success'
                });
                this.reset();
                this.$emit('uploaded');
            }).catch((error) => {
                console.error('Error uploading file:', error);
                this.errorMessage = error.response?.data?.error || 'Error uploading file. Please try again.';
            }).finally(() => {
                this.uploading = false;
            });
        }
    },
    watch: {
        isOpen(open) {
            if (open) this.reset();
        }
    }
};
</script>

<style scoped>
    /* Multi-line label: keep the check glyph on the first line, not centered */
    .stem-checkbox {
        align-items: flex-start;
    }
    .stem-checkbox ::v-deep .check {
        margin-top: 3px;
    }
</style>
